/**
 * quickKeybindStore.ts
 *
 * Coordinates:
 *  1. Action bar slot highlighting when hovering `Bar B · Slot S` rows in the
 *     Key Bindings window OR when hovering HUD action bar slots directly.
 *  2. ElvUI-style `/kb` (`/keybind`) Quick Keybind Mode: hover any action bar
 *     slot on the HUD and press a key (or Shift/Ctrl/Alt + key) to bind it on
 *     the fly, Backspace/Delete to clear, or Escape to exit.
 */

import { useSyncExternalStore } from 'react';
import { DEFAULT_KEY_BINDINGS } from '../../../../config/input/InputBindings';
import type { KeyBinding } from '../../../../shared/CharacterTypes';
import { gameLogger } from '../../../../utils/GameLogger';
import { KeyBindingManager } from '../../../services/KeyBindingManager';
import { findBindingConflict } from '../../../systems/input/heldAimInput';
import { formatKeyBinding } from '../../../systems/input/keyLabels';
import { ACTION_METADATA } from '../../Windows/Settings/keybindingGroups';
import {
  bindingId,
  domEventToBindingKey,
  eventModifier,
  findKeyHolder,
  isModifierCode,
} from '../../Windows/Settings/keybindingKeymap';

export interface HoveredBarSlot {
  bar: number;
  slot: number;
}

export interface QuickKeybindSnapshot {
  /** Slot hovered/capturing in the Key Bindings window table. */
  tableHoveredSlot: HoveredBarSlot | null;
  /** Slot currently hovered by the mouse pointer on the HUD. */
  hudHoveredSlot: HoveredBarSlot | null;
  /** Whether `/kb` Quick Keybind Mode is active. */
  quickKeybindActive: boolean;
  /** Status message displayed in the Quick Keybind Mode banner. */
  statusMessage: string | null;
  statusTone: 'info' | 'success' | 'error';
}

const BAR_SLOT_ACTION_RE = /^bar(\d+)_slot(\d+)$/;

export function parseBarSlotAction(action: string | null | undefined): HoveredBarSlot | null {
  if (!action) return null;
  const match = BAR_SLOT_ACTION_RE.exec(action);
  if (!match) return null;
  return { bar: Number(match[1]), slot: Number(match[2]) };
}

const actionLabel = (action: string): string =>
  ACTION_METADATA[action]?.label ?? action.replace(/_/g, ' ');

class QuickKeybindStore {
  private snapshot: QuickKeybindSnapshot = {
    tableHoveredSlot: null,
    hudHoveredSlot: null,
    quickKeybindActive: false,
    statusMessage: null,
    statusTone: 'info',
  };

  private listeners = new Set<() => void>();

  public getSnapshot = (): QuickKeybindSnapshot => this.snapshot;

  public subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  private emit(next: Partial<QuickKeybindSnapshot>): void {
    this.snapshot = { ...this.snapshot, ...next };
    for (const listener of this.listeners) {
      listener();
    }
  }

  public setTableHoveredAction(action: string | null): void {
    const parsed = parseBarSlotAction(action);
    const prev = this.snapshot.tableHoveredSlot;
    if (prev?.bar === parsed?.bar && prev?.slot === parsed?.slot) return;
    this.emit({ tableHoveredSlot: parsed });
  }

  public setHudHoveredSlot(slot: HoveredBarSlot | null): void {
    const prev = this.snapshot.hudHoveredSlot;
    if (prev?.bar === slot?.bar && prev?.slot === slot?.slot) return;
    this.emit({ hudHoveredSlot: slot });
  }

  public toggleQuickKeybindMode(force?: boolean): boolean {
    const nextActive = force !== undefined ? force : !this.snapshot.quickKeybindActive;
    this.emit({
      quickKeybindActive: nextActive,
      statusMessage: nextActive
        ? 'Hover any action bar slot and press a key (or Shift/Ctrl/Alt + key) to bind.'
        : null,
      statusTone: 'info',
    });
    return nextActive;
  }

  public clearHoveredSlotBinding(): boolean {
    const target = this.snapshot.hudHoveredSlot;
    if (!target) return false;
    const action = `bar${target.bar}_slot${target.slot}`;
    const mgr = KeyBindingManager.getInstance();
    const live = mgr.getBindings();
    const existingIdx = live.findIndex((b) => b.action === action);
    if (existingIdx === -1) {
      this.emit({
        statusMessage: `Bar ${target.bar} · Slot ${target.slot} is already unbound.`,
        statusTone: 'info',
      });
      return true;
    }
    live.splice(existingIdx, 1);
    mgr.saveBindings();
    this.emit({
      statusMessage: `Cleared keybind on Bar ${target.bar} · Slot ${target.slot}.`,
      statusTone: 'success',
    });
    gameLogger.info('input', `[QuickKeybind] cleared ${action}`);
    return true;
  }

  public bindHoveredSlot(newKey: string | null, modifier?: KeyBinding['modifier']): boolean {
    const target = this.snapshot.hudHoveredSlot;
    if (!target) {
      this.emit({
        statusMessage: 'Hover your mouse over an action bar slot first, then press a key.',
        statusTone: 'error',
      });
      return false;
    }
    if (!newKey) {
      this.emit({
        statusMessage: 'That key is not bindable. Try a letter, digit, arrow, Space, or Tab.',
        statusTone: 'error',
      });
      return false;
    }

    const action = `bar${target.bar}_slot${target.slot}`;
    const mgr = KeyBindingManager.getInstance();
    const live = mgr.getBindings();
    const existingPrimary = live.find((b) => b.action === action);

    const candidate: KeyBinding = {
      ...(existingPrimary ?? { action, key: newKey, category: 'combat' }),
      modifier,
    };

    const freeAimAlt = modifier === 'alt'
      ? live.find((b) => b.action === 'free_aim' && b.key.toUpperCase() === 'ALT') ?? null
      : null;
    const conflict = findBindingConflict(candidate, newKey, live)
      ?? freeAimAlt
      ?? findKeyHolder(action, newKey, modifier, live, DEFAULT_KEY_BINDINGS.bindings);

    if (conflict) {
      this.emit({
        statusMessage: `${formatKeyBinding({ key: newKey, modifier })} conflicts with ${actionLabel(conflict.action)}.`,
        statusTone: 'error',
      });
      return false;
    }

    const chord = modifier ? { key: newKey, modifier } : { key: newKey };
    if (existingPrimary) {
      const idx = live.findIndex((b) => bindingId(b) === bindingId(existingPrimary));
      if (idx !== -1) {
        live[idx] = { action, category: existingPrimary.category ?? 'combat', ...chord };
      } else {
        live.push({ action, category: 'combat', ...chord });
      }
    } else {
      live.push({ action, category: 'combat', ...chord });
    }

    mgr.saveBindings();
    const formatted = formatKeyBinding(chord);
    this.emit({
      statusMessage: `Bound Bar ${target.bar} · Slot ${target.slot} to ${formatted}.`,
      statusTone: 'success',
    });
    gameLogger.info('input', `[QuickKeybind] bound ${action} -> ${formatted}`);
    return true;
  }

  public handleKeyDownInQuickMode(e: KeyboardEvent, pendingModifierRef: { current: string | null }): void {
    if (!this.snapshot.quickKeybindActive) return;

    if (e.code === 'Escape' || e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      pendingModifierRef.current = null;
      this.toggleQuickKeybindMode(false);
      return;
    }

    if (!this.snapshot.hudHoveredSlot) return;

    e.preventDefault();
    e.stopPropagation();

    if (e.code === 'Backspace' || e.code === 'Delete') {
      pendingModifierRef.current = null;
      this.clearHoveredSlotBinding();
      return;
    }

    if (isModifierCode(e.code)) {
      pendingModifierRef.current = e.code;
      return;
    }

    pendingModifierRef.current = null;
    this.bindHoveredSlot(domEventToBindingKey(e), eventModifier(e));
  }

  public handleKeyUpInQuickMode(e: KeyboardEvent, pendingModifierRef: { current: string | null }): void {
    if (!this.snapshot.quickKeybindActive) return;
    if (pendingModifierRef.current !== e.code) return;
    if (!this.snapshot.hudHoveredSlot) {
      pendingModifierRef.current = null;
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    pendingModifierRef.current = null;
    this.bindHoveredSlot(domEventToBindingKey(e));
  }
}

export const quickKeybindStore = new QuickKeybindStore();

export function useQuickKeybindSnapshot(): QuickKeybindSnapshot {
  return useSyncExternalStore(quickKeybindStore.subscribe, quickKeybindStore.getSnapshot);
}
